import clsx from 'clsx'
import renderSelect from 'components/FormControls/select'
import renderInputField from 'components/FormControls/textInput'
import GridSpinner from 'components/UI/GridSpinner'
import { Icon, Tooltip } from 'gu-ui-kit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import emailValidator from 'utils/validators/emailValidator'
import emptyArrayValidator from 'utils/validators/emptyArrayValidator'
import urlValidator from 'utils/validators/urlValidator'
import * as Yup from 'yup'
import SERVICES_API_V1 from 'api/services/v1'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import domainValidator from 'utils/validators/domainValidator'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'

export const AppnextSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    network: Yup.string().required('common::validation_messages.required'),
    google_play_url: Yup.string()
      .required('common::validation_messages.required')
      .test('urlValidator', '', urlValidator),
    geo: Yup.array()
      .required('common::validation_messages.required')
      .test('min', '', emptyArrayValidator),
    mmp: Yup.array()
      .required('common::validation_messages.required')
      .test('min', '', emptyArrayValidator),
    kpi: Yup.string().required('common::validation_messages.required'),
    category: Yup.string().required('common::validation_messages.required'),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    event_name: Yup.string().required('common::validation_messages.required'),
    advertiser_name: Yup.string().required('common::validation_messages.required'),
    advertiser_juridical_name: Yup.string().required('common::validation_messages.required'),
    advertiser_site: Yup.string().test(
      'domainValidator',
      'common::validation_messages.incorrect_url',
      domainValidator,
    ),
  })

const MMP_OPTIONS = [
  {
    value: 'appmetrica',
    label: 'Appmetrica',
  },
  {
    value: 'appsflyer',
    label: 'Appsflyer',
  },
  {
    value: 'adjust',
    label: 'Adjust',
  },
  {
    value: 'mytracker',
    label: 'Mytracker',
  },
  {
    value: 'branch',
    label: 'Branch',
  },
  {
    value: 'kochava',
    label: 'Kochava',
  },
  {
    value: 'tenjin',
    label: 'Tenjin',
  },
]

let CreateAppnextAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkId,
  form,
  initialValues,
  array,
  multiLogins,
  networkType,
}) => {
  const selector = formValueSelector(form)
  const { t } = useTranslation()
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [countries, setCountries] = useState([])

  const { email, user } = useSelector((state) => ({
    email: selector(state, 'email'),
    user: state['app'].user,
  }))

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)

      if (countries.length === 0) {
        SERVICES_API_V1.getNetworkSettings({ networkId })
          .then(({ response: { data } }) => {
            setCountries(
              (data?.countries || []).map(({ id, name }) => ({
                value: id,
                label: name,
              })),
            )
          })
          .finally(() => {
            setSettingsLoading(false)
          })
      }
    }
  }, [networkId])

  useEffect(() => {
    user.name && email === initialValues.email && autofill('email', user.name)
  }, [user])

  return (
    <FormFrame
      onSubmit={handleSubmit(submitAccountData)}
      multiLogins={multiLogins}
      form={form}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
      <Field
        disabled
        name="network"
        hidden
        fullWidth
        component={renderInputField}
        t={t}
      />

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <LoginField
          form={form}
          multiLogins={multiLogins}
          array={array}
          autofill={autofill}
          networkId={networkId}
          label={'services::forms.register_account.appnext.fields.login.label'}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="email"
          type="email"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.email.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="google_play_url"
          fullWidth
          type={'url'}
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.google_play_url.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="category"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.category.label')}
          t={t}
        />
        <div className={formStyles['tooltip-container']}>
          <Tooltip
            title={t('services::forms.register_account.appnext.fields.category.tooltip')}
            placement="top"
            arrow
          >
            <Icon
              name={'information'}
              color={'Label-Secondary'}
              size={'m'}
            />
          </Tooltip>
        </div>
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          label={t('services::forms.register_account.appnext.fields.geo.label')}
          name="geo"
          multiple
          showEmptyOption={false}
          component={renderSelect}
          readOnly={settingsLoading}
          fullWidth
          t={t}
          normalize={(value = []) => (value?.length === 0 ? null : value)}
          options={countries}
          mode={'filter'}
          emptyOptionLabel={t('common::select_empty_option')}
          noResultsLabel={t('common::empty_results.header')}
          ControlProps={{
            startAdornment: settingsLoading && <GridSpinner width={24} />,
            dropSelection: t('common::buttons.reset'),
            apply: t('common::buttons.apply'),
          }}
        />
        <div className={formStyles['tooltip-container']}>
          <Tooltip
            title={t('services::forms.register_account.appnext.fields.geo.tooltip')}
            placement="top"
            arrow
          >
            <Icon
              name={'information'}
              color={'Label-Secondary'}
              size={'m'}
            />
          </Tooltip>
        </div>
      </div>
      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          label={t('services::forms.register_account.appnext.fields.mmp.label')}
          name="mmp"
          component={renderSelect}
          fullWidth
          t={t}
          normalize={(value = []) => (value?.length === 0 ? null : value)}
          options={MMP_OPTIONS}
          mode={'default'}
          multiple
          emptyOptionLabel={t('common::select_clear_option')}
          noResultsLabel={t('common::empty_results.header')}
          ControlProps={{
            dropSelection: t('common::buttons.reset'),
            apply: t('common::buttons.apply'),
          }}
        />
        <div className={formStyles['tooltip-container']}>
          <Tooltip
            title={t('services::forms.register_account.appnext.fields.mmp.tooltip')}
            placement="top"
            arrow
          >
            <Icon
              name={'information'}
              color={'Label-Secondary'}
              size={'m'}
            />
          </Tooltip>
        </div>
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="kpi"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.kpi.label')}
          t={t}
        />
        <div className={formStyles['tooltip-container']}>
          <Tooltip
            title={t('services::forms.register_account.appnext.fields.kpi.tooltip')}
            placement="top"
            arrow
          >
            <Icon
              name={'information'}
              color={'Label-Secondary'}
              size={'m'}
            />
          </Tooltip>
        </div>
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="event_name"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.event_name.label')}
          t={t}
        />
        <div className={formStyles['tooltip-container']}>
          <Tooltip
            title={t('services::forms.register_account.appnext.fields.event_name.tooltip')}
            placement="top"
            arrow
          >
            <Icon
              name={'information'}
              color={'Label-Secondary'}
              size={'m'}
            />
          </Tooltip>
        </div>
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="advertiser_name"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.advertiser_name.label')}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="advertiser_juridical_name"
          type="text"
          fullWidth
          component={renderInputField}
          label={t(
            'services::forms.register_account.appnext.fields.advertiser_juridical_name.label',
          )}
          t={t}
        />
      </div>

      <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
        <Field
          name="advertiser_site"
          type="text"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.appnext.fields.advertiser_site.label')}
          t={t}
        />
      </div>
    </FormFrame>
  )
}

CreateAppnextAccountForm = reduxForm({
  form: 'CreateAppnextAccountForm',
  initialValues: {
    network: '',
    login: '',
    email: '',
    mmp: null,
    category: '',
    geo: null,
    kpi: '',
    event_name: '',
    advertiser_name: '',
    advertiser_juridical_name: '',
    advertiser_site: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAppnextAccountForm)

CreateAppnextAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  networkType: PropTypes.string,
}

export default CreateAppnextAccountForm
